import React, { useContext, useState } from "react";
import { useMutation } from "@tanstack/react-query";

//Common
import { Snackbar } from "../common/VFX-UI";

//App
import agencyApi from "../../app/model/api/agencyApi";
import { AgencyContext } from "./AgencyContainer";
import { EMPTY_GUID } from "../../utility/constants/constants";
import { apiErrorMessage } from "../../utility/lib/error";
import { AgencyFormContainerProps } from "../../types/agency";
import { SnackbarState } from "../../types";
import { AgencyViewModel } from "../../viewModels/agencyViewModel";
import { AgencyDtoDto } from "../../app/model/api/types/dto/agencyDto";
import AgencyForm from "./AgencyForm";

export const AgencyFormContainer: React.FC<
  AgencyFormContainerProps
> = (props) => {
  const { onClose, onShowSuccess } = props;
  const agencyContext = useContext(AgencyContext);

  const [snackbar, setSnackbar] = useState<SnackbarState>({
    showErrorSnackbar: false,
    errorMessage: "",
  });

  const successMessage = agencyContext?.id
    ? "Agency updated"
    : "New Agency added";

  const onShowErrorSnackbar = (errorMessage: string) => {
    setSnackbar({
      showErrorSnackbar: true,
      errorMessage,
    });
  };

  const onCloseSnackbar = () => {
    setSnackbar({
      showErrorSnackbar: false,
      errorMessage: "",
    });
  };

  const { status, mutate } = useMutation<void, Error, AgencyDtoDto>({
    mutationFn: async (payload: AgencyDtoDto) => {
      const response = await agencyApi.saveAgency(payload);
      const errorMessage = apiErrorMessage(response.errorMessages);
      if (errorMessage) {
        onShowErrorSnackbar(errorMessage);
      } else {
        onShowSuccess(successMessage);
      }
    },
  });

  const handleOnSubmit = async (values: AgencyViewModel) => {
    const agency: AgencyDtoDto = values.toDto();
    mutate(agency);
  };

  const initialValues = agencyContext?.id
    ? new AgencyViewModel(
        agencyContext.data?.id ?? null,
        agencyContext.data?.name ?? null,
        agencyContext.data?.description ?? null,
        agencyContext.data?.sortValue ?? null,
        agencyContext.data?.isActive ?? null
      )
    : new AgencyViewModel(
        EMPTY_GUID,
        "",
        "",
        0,
        true
      );

  return (
    <>
      <AgencyForm
        initialVal={initialValues}
        status={status}
        handleOnSubmit={handleOnSubmit}
        onClose={onClose}
      />
      {snackbar.showErrorSnackbar && (
        <Snackbar
          open={true}
          message={snackbar.errorMessage}
          handleClose={onCloseSnackbar}
          severity="danger"
        />
      )}
    </>
  );
};
